import { Button, Css, Icon, Palette, useTestIds } from "@homebound/beam";
import {
  ProductOfferingRecentChanges_ProductOfferingFragment,
  ProductOfferingRecentChangesQuery,
  useProductOfferingRecentChangesQuery,
} from "src/routes/libraries/product-offerings/overview/ProductOfferingRecentChanges.generated";
import { createProjectProductOfferingVersionHistoryUrl } from "src/RouteUrls";
import { queryResult, sanitizeHtml } from "src/utils";
import { formatDistanceToNow } from "src/utils/dates";

type ProductOfferingRecentChangesProps = { productOffering: ProductOfferingRecentChanges_ProductOfferingFragment };

/**
 * Plan and design package changes in the latest product offering version.
 */
export function ProductOfferingRecentChanges(props: ProductOfferingRecentChangesProps) {
  const { productOffering: po } = props;
  const tid = useTestIds({}, "recentChanges");
  // Diffs the latest version against the one before it, whether either is draft, active, or archived
  const latestVersion = po.aggregateVersions.find((v) => v.id === po.aggregateLatest.id)?.version ?? 0;
  const previousVersionId = po.aggregateVersions
    .filter((v) => (v.version ?? 0) < latestVersion)
    .sortBy((v) => v.version ?? 0).last?.id;
  const query = useProductOfferingRecentChangesQuery({
    variables: { id: po.id, versionId: previousVersionId ?? "" },
    skip: !previousVersionId,
    // The result is the same ProductOffering pinned to an older version; caching it would overwrite the panel's
    // latest-version package versions
    fetchPolicy: "no-cache",
  });

  function renderChanges(previous: ProductOfferingRecentChangesQuery["productOffering"] | undefined) {
    const changes = [
      { label: "Plan package", latest: po.planPackageVersion, previous: previous?.planPackageVersion },
      {
        label: "Design package",
        latest: po.interiorDesignPackageVersion,
        previous: previous?.interiorDesignPackageVersion,
      },
      {
        label: "Design package",
        latest: po.exteriorDesignPackageVersion,
        previous: previous?.exteriorDesignPackageVersion,
      },
    ].filter((c) => c.latest.id !== c.previous?.id);
    if (changes.isEmpty) return <div css={Css.xs.gray700.pyPx(12).$}>No package changes in the latest version</div>;
    return (
      <>
        {changes.map(({ label, latest, previous }) => (
          <div key={latest.id} css={Css.df.gapPx(10).pyPx(12).bb.bcGray200.xs.$} {...tid.change}>
            <Icon icon="aiStar" color={Palette.Purple600} inc={2} xss={Css.fs0.$} />
            <div css={Css.df.fdc.gapPx(2).fg1.mw0.$}>
              <div css={Css.df.jcsb.gap1.$}>
                <div>
                  <span css={Css.xsSb.$}>{label}</span>
                  <span css={Css.gray700.$}>
                    {" · "}
                    {previous ? `${previous.displayVersion} → ` : ""}
                    {latest.displayVersion}
                  </span>
                </div>
                <div css={Css.gray700.wsnw.$}>
                  {formatDistanceToNow(po.aggregateLatest.createdAt, { addSuffix: true })}
                </div>
              </div>
              {/* The publish modal saves notes from a RichTextField, so they're HTML */}
              {latest.versionNotes && <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(latest.versionNotes) }} />}
            </div>
          </div>
        ))}
      </>
    );
  }

  return (
    <div css={Css.df.fdc.gapPx(4).$} {...tid}>
      <div css={Css.df.aic.jcsb.$}>
        <div css={Css.mdSb.$}>Recent changes</div>
        {po.project && (
          <Button
            label="View all"
            variant="text"
            size="sm"
            onClick={createProjectProductOfferingVersionHistoryUrl(po.id, po.project.id)}
            {...tid.viewAll}
          />
        )}
      </div>
      {/* The first version has nothing to diff against, so all of its packages are listed */}
      {previousVersionId ? queryResult(query, (data) => renderChanges(data.productOffering)) : renderChanges(undefined)}
    </div>
  );
}
